import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Download, ExternalLink, History } from 'lucide-react';
import { toast } from 'sonner';

/**
 * iter132 — inline preview modal for R&D attachments.
 * Renders PDFs in an iframe and images as <img>. Falls back to a download
 * button for anything else. Also exposes a Version History dropdown so
 * teammates can roll back to an earlier upload.
 */
export default function RDAttachmentPreview({ projectId, attachment, open, onClose }) {
  const { token, api } = useAuth();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const ct = (attachment?.content_type || '').toLowerCase();
  const isImage = ct.startsWith('image/');
  const isPdf = ct === 'application/pdf' || (attachment?.filename || '').toLowerCase().endsWith('.pdf');
  const isText = ct.startsWith('text/') || ct === 'application/json';
  const canPreview = isImage || isPdf || isText;

  const buildUrl = (ver = null) => {
    if (!attachment) return '';
    if (ver == null || ver === attachment.version) {
      return `${base}/api/rd/projects/${projectId}/attachments/${attachment.id}/download?inline=true`;
    }
    return `${base}/api/rd/projects/${projectId}/attachments/${attachment.id}/versions/${ver}/download?inline=true`;
  };

  useEffect(() => {
    let cancelled = false;
    if (!open || !attachment?.storage_path || !canPreview) { setBlobUrl(null); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(buildUrl(null), { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const b = await res.blob();
        if (!cancelled) setBlobUrl(URL.createObjectURL(b));
      } catch (e) {
        if (!cancelled) toast.error('Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [open, attachment?.id, attachment?.version]);

  useEffect(() => {
    // Clean up object URL when it changes or modal closes
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  useEffect(() => {
    if (!open || !attachment?.storage_path) return;
    api().get(`/rd/projects/${projectId}/attachments/${attachment.id}/versions`)
      .then(r => setVersions(r.data || []))
      .catch(() => setVersions([]));
    // eslint-disable-next-line
  }, [open, attachment?.id]);

  const downloadVersion = async (ver) => {
    const res = await fetch(buildUrl(ver).replace('inline=true', 'inline=false'), { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (versions.find(v => v.version === ver)?.filename) || `v${ver}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  if (!attachment) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="rd-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="truncate">{attachment.filename}</span>
            {attachment.version > 1 && (
              <span className="text-[10px] bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 px-1.5 py-0.5 rounded">v{attachment.version}</span>
            )}
            <Button size="sm" variant="outline" onClick={() => setShowHistory(s => !s)} className="ml-auto" data-testid="rd-preview-history-toggle">
              <History className="w-3.5 h-3.5 mr-1" /> {versions.length} version{versions.length === 1 ? '' : 's'}
            </Button>
          </DialogTitle>
        </DialogHeader>

        {showHistory && (
          <div className="border border-slate-200 rounded max-h-40 overflow-y-auto bg-slate-50 text-xs">
            {versions.length === 0 ? (
              <div className="p-3 text-slate-500">No versions yet.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-2 py-1">Version</th>
                    <th className="text-left px-2 py-1">Filename</th>
                    <th className="text-left px-2 py-1">Uploaded</th>
                    <th className="text-left px-2 py-1">By</th>
                    <th className="text-right px-2 py-1">Size</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => (
                    <tr key={v.version} className="border-t border-slate-200" data-testid={`rd-version-row-${v.version}`}>
                      <td className="px-2 py-1 font-mono">v{v.version}{v.is_current ? ' (current)' : ''}</td>
                      <td className="px-2 py-1 truncate">{v.filename}</td>
                      <td className="px-2 py-1">{(v.uploaded_at || '').slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-1">{v.uploaded_by_name || '—'}</td>
                      <td className="px-2 py-1 text-right">{v.size_bytes ? `${(v.size_bytes / 1024).toFixed(1)} KB` : '—'}</td>
                      <td className="px-2 py-1 text-right">
                        <button
                          onClick={() => downloadVersion(v.version)}
                          className="text-blue-600 hover:underline"
                          data-testid={`rd-version-download-${v.version}`}
                        ><Download className="w-3 h-3 inline" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="min-h-[300px]">
          {loading && <div className="text-sm text-slate-500 p-6 text-center">Loading preview…</div>}
          {!loading && isImage && blobUrl && (
            <img src={blobUrl} alt={attachment.filename} className="max-w-full max-h-[70vh] mx-auto object-contain" data-testid="rd-preview-image" />
          )}
          {!loading && isPdf && blobUrl && (
            <iframe
              src={blobUrl}
              title={attachment.filename}
              className="w-full"
              style={{ height: '70vh' }}
              data-testid="rd-preview-pdf"
            />
          )}
          {!loading && isText && blobUrl && (
            <iframe
              src={blobUrl}
              title={attachment.filename}
              className="w-full border border-slate-200"
              style={{ height: '50vh' }}
              data-testid="rd-preview-text"
            />
          )}
          {!loading && !canPreview && (
            <div className="text-center py-10 text-slate-500 text-sm" data-testid="rd-preview-fallback">
              <p>No inline preview for this file type.</p>
              <Button size="sm" className="mt-3" onClick={() => downloadVersion(attachment.version)} data-testid="rd-preview-download-btn">
                <Download className="w-4 h-4 mr-1" />Download {attachment.filename}
              </Button>
            </div>
          )}
          {!loading && canPreview && blobUrl && (
            <div className="flex justify-end gap-2 mt-3">
              <a
                href={blobUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-slate-500 hover:text-blue-600 inline-flex items-center gap-1"
                data-testid="rd-preview-open-tab"
              ><ExternalLink className="w-3 h-3" /> Open in new tab</a>
              <button
                onClick={() => downloadVersion(attachment.version)}
                className="text-xs text-slate-500 hover:text-blue-600 inline-flex items-center gap-1"
                data-testid="rd-preview-download"
              ><Download className="w-3 h-3" /> Download</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
